"use client";

import { useState, useEffect, useCallback } from "react";

type ConsentState = "pending" | "accepted" | "rejected";

const CONSENT_KEY = "closerai-cookie-consent";

function updateGoogleConsent(granted: boolean) {
  if (typeof window === "undefined") return;
  const value = granted ? "granted" : "denied";
  window.gtag?.("consent", "update", {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

export function CookieConsent() {
  const [state, setState] = useState<ConsentState>("pending");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "accepted") {
      setState("accepted");
      updateGoogleConsent(true);
    } else if (stored === "rejected") {
      setState("rejected");
    } else {
      setVisible(true);
    }
  }, []);

  const accept = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setState("accepted");
    setVisible(false);
    updateGoogleConsent(true);
  }, []);

  const reject = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    setState("rejected");
    setVisible(false);
    updateGoogleConsent(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie-instellingen"
      className="fixed right-0 bottom-0 left-0 z-[9999] p-4 sm:p-6"
    >
      <div className="mx-auto max-w-2xl rounded-2xl border border-white/10 bg-gray-900/95 p-5 shadow-2xl backdrop-blur-xl sm:p-6">
        <p className="mb-4 text-sm leading-relaxed text-gray-300">
          Wij gebruiken cookies voor websiteanalyse en het verbeteren van je ervaring. Je kunt
          cookies accepteren of weigeren.{" "}
          <a
            href="/privacy"
            className="underline underline-offset-2 transition-colors hover:text-white"
          >
            Privacybeleid
          </a>
        </p>
        <div className="flex gap-3">
          <button
            onClick={accept}
            className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-[box-shadow,filter,transform] hover:brightness-110 focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 focus-visible:outline-none active:scale-[0.98]"
          >
            Accepteren
          </button>
          <button
            onClick={reject}
            className="rounded-xl border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-medium text-gray-300 transition-[background-color,color,transform] hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 focus-visible:outline-none active:scale-[0.98]"
          >
            Weigeren
          </button>
        </div>
      </div>
    </div>
  );
}
